// DocumentCategory API Interface
import { useKeycloak } from '@react-keycloak/web';
import { useMemo } from 'react';

// Helper function to extract realm ID from Keycloak token
const extractRealmIdFromToken = (tokenParsed: any) => {
  if (!tokenParsed) return null;
  
  // Extract from issuer: "https://auth.webelievesoftware.com/auth/realms/edgelex-realm"
  const issuer = tokenParsed.iss;
  if (issuer) {
    const realmMatch = issuer.match(/realms\/(.+)$/);
    return realmMatch ? realmMatch[1] : null;
  }
  
  return null;
};

export interface DocumentCategory {
  id: string;
  name: string;
  description?: string;
  parent_id?: string;
  category_type: 'procedural_filing' | 'contract_agreement' | 'corporate_business' | 'discovery_disclosure' | 'family_law' | 'real_estate' | 'criminal_law' | 'administrative' | 'estate_planning' | 'legal_opinion' | 'draft_template';
  practice_areas?: string[];
  jurisdictions?: string[];
  confidentiality_level?: 'public' | 'internal' | 'confidential' | 'attorney_client_privileged' | 'attorney_work_product';
  default_stage?: 'draft' | 'review' | 'approved' | 'executed' | 'filed' | 'archived';
  level?: number;
  sort_order?: number;
  display_order?: number;
  color_code?: string;
  icon_name?: string;
  ai_keywords?: string[];
  retention_period_days?: number;
  is_system_category: boolean;
  is_active: boolean;
  usage_count: number;
  realm_id: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  parent?: DocumentCategory;
  children?: DocumentCategory[];
  documents?: any[];
}

export interface CreateDocumentCategoryRequest {
  name: string;
  description?: string;
  parent_id?: string;
  category_type: DocumentCategory['category_type'];
  practice_areas?: string[];
  jurisdictions?: string[];
  confidentiality_level?: DocumentCategory['confidentiality_level'];
  default_stage?: DocumentCategory['default_stage'];
  ai_keywords?: string[];
  retention_period_days?: number;
}

// Hook for document category API with Keycloak authentication
export const useDocumentCategoryAPI = () => {
  const { keycloak } = useKeycloak();
  const realmId = extractRealmIdFromToken(keycloak.tokenParsed);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${keycloak.token}`,
    'Content-Type': 'application/json',
  });

  const baseUrl = window.location.origin;

  // Get all document categories
  const getDocumentCategories = async (): Promise<DocumentCategory[]> => {
    const response = await fetch(`${baseUrl}/api/v1/dms/categories?realmId=${realmId}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch document categories: ${response.statusText}`);
    }

    const data = await response.json();
    return data.categories || data.data || [];
  };

  // Get categories hierarchy
  const getCategoriesHierarchy = async (): Promise<DocumentCategory[]> => {
    const response = await fetch(`${baseUrl}/api/v1/dms/categories/hierarchy?realmId=${realmId}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch categories hierarchy: ${response.statusText}`);
    }

    const data = await response.json();
    return data.categories || data.data || [];
  };

  // Get category icon
  const getCategoryIcon = (categoryType: DocumentCategory['category_type']): string => {
    const iconMap: Record<DocumentCategory['category_type'], string> = {
      'procedural_filing': '🏛',
      'contract_agreement': '🖋',
      'corporate_business': '📑',
      'discovery_disclosure': '📄',
      'family_law': '👨‍👩‍👧‍👦',
      'real_estate': '🏠',
      'criminal_law': '⚖️',
      'administrative': '🏛',
      'estate_planning': '📜',
      'legal_opinion': '📝',
      'draft_template': '🔄'
    };
    
    return iconMap[categoryType] || '📄';
  };

  // Get human-readable category type name
  const getCategoryTypeName = (categoryType: DocumentCategory['category_type']): string => {
    const nameMap: Record<DocumentCategory['category_type'], string> = {
      'procedural_filing': 'Procedural Filings',
      'contract_agreement': 'Contracts and Agreements',
      'corporate_business': 'Corporate/Business Documents',
      'discovery_disclosure': 'Discovery and Disclosure',
      'family_law': 'Family Law Documents',
      'real_estate': 'Real Estate',
      'criminal_law': 'Criminal Law Documents',
      'administrative': 'Administrative/Regulatory Filings',
      'estate_planning': 'Estate & Probate',
      'legal_opinion': 'Legal Opinions & Internal Memos',
      'draft_template': 'Templates/Drafts/Redlines'
    };
    
    return nameMap[categoryType] || categoryType.replace(/_/g, ' ');
  };

  return useMemo(() => ({
    getDocumentCategories,
    getCategoriesHierarchy,
    getCategoryIcon,
    getCategoryTypeName,
    realmId,
  }), [keycloak.token, realmId]);
}; 
